import type { QuestionDefinition, QuestionFlow, QuestionId } from "@/lib/types/questions";

export const QUESTION_META: Record<QuestionId, QuestionDefinition> = {
  destination: {
    id: "destination",
    title: "Nereye gitmek istersiniz?",
    subtitle: "Bir rota kafanızda varsa söyleyin, yoksa biz önerelim.",
    required: true,
  },
  departure: {
    id: "departure",
    title: "Nereden yola çıkıyorsunuz?",
    subtitle: "Başlangıç noktanızı öğrenelim.",
    required: true,
  },
  dates: {
    id: "dates",
    title: "Ne zaman gitmeyi düşünüyorsunuz?",
    subtitle: "Tarih aralığı ya da esnek dönem seçin.",
    required: true,
  },
  travelers: {
    id: "travelers",
    title: "Kaç kişi, kimlerle?",
    subtitle: "Yetişkin ve çocuk sayısıyla seyahat tipini belirleyelim.",
    required: true,
  },
  transport: {
    id: "transport",
    title: "Nasıl seyahat edeceksiniz?",
    subtitle: "Uçak, araç, karavan, gulet... Tercihiniz ne?",
    required: true,
  },
  budget: {
    id: "budget",
    title: "Bütçe bandınız ne?",
    subtitle: "Konforu belirleyelim, sonra detaylandırırız.",
    required: true,
  },
  vehicleInfo: {
    id: "vehicleInfo",
    title: "Aracınızın bilgileri",
    subtitle: "Yakıt tüketimi ve maliyeti doğru hesaplamak için.",
    required: false,
  },
  transitStops: {
    id: "transitStops",
    title: "Yol üzeri duraklar",
    subtitle: "Ara ülkelerde durup keşfetmek ister misiniz?",
    required: false,
  },
  campingPreferences: {
    id: "campingPreferences",
    title: "Kamp tercihleri",
    subtitle: "Karavan park, doğa kampı ya da karma?",
    required: false,
  },
  guletPreferences: {
    id: "guletPreferences",
    title: "Gulet tercihleri",
    subtitle: "Kabin sayısı, mürettebat ve su sporları.",
    required: false,
  },
  waterSports: {
    id: "waterSports",
    title: "Su sporları",
    subtitle: "Dalış, jet-ski, wakeboard ilginizi çeker mi?",
    required: false,
  },
  cruisePreferences: {
    id: "cruisePreferences",
    title: "Cruise tercihleri",
    subtitle: "Paket tipi ve kabin kategorisi.",
    required: false,
  },
  fitnessLevel: {
    id: "fitnessLevel",
    title: "Fiziksel kondisyon",
    subtitle: "Rotayı buna göre kurgulayalım.",
    required: false,
  },
  bikeType: {
    id: "bikeType",
    title: "Bisiklet tipi",
    subtitle: "Yol, dağ veya e-bike?",
    required: false,
  },
  altitudeComfort: {
    id: "altitudeComfort",
    title: "Yükseklik konforu",
    subtitle: "Yüksek irtifa sizi zorlar mı?",
    required: false,
  },
  accommodation: {
    id: "accommodation",
    title: "Nerede kalmak istersiniz?",
    subtitle: "Lüks otel, butik, airbnb, kamp... Tarzınız ne?",
    required: true,
  },
  interests: {
    id: "interests",
    title: "Nelerle ilgileniyorsunuz?",
    subtitle: "Birden fazla seçebilirsiniz.",
    required: true,
  },
  pace: {
    id: "pace",
    title: "Tempo tercihiniz",
    subtitle: "Dolu mu, dengeli mi, rahat mı olsun?",
    required: true,
  },
  food: {
    id: "food",
    title: "Yemek tercihleri",
    subtitle: "Yerel keşif mi, bildiğiniz lezzet mi?",
    required: true,
  },
  childFriendlyPriorities: {
    id: "childFriendlyPriorities",
    title: "Çocuk dostu öncelikler",
    subtitle: "Havuz, animasyon, tema park...",
    required: false,
  },
  adventureTypes: {
    id: "adventureTypes",
    title: "Macera tipleri",
    subtitle: "Trekking, dalış, safari, paraşüt...",
    required: false,
  },
  wellnessTypes: {
    id: "wellnessTypes",
    title: "Wellness odakları",
    subtitle: "Spa, yoga, detoks, tedavi...",
    required: false,
  },
  culturalInterests: {
    id: "culturalInterests",
    title: "Kültürel ilgiler",
    subtitle: "Arkeoloji, modern sanat, mimari...",
    required: false,
  },
  cuisineTypes: {
    id: "cuisineTypes",
    title: "Mutfak tercihleri",
    subtitle: "Hangi mutfakları keşfetmek istersiniz?",
    required: false,
  },
  cookingClasses: {
    id: "cookingClasses",
    title: "Yemek atölyesi",
    subtitle: "Şefle atölyeye katılmak ister misiniz?",
    required: false,
  },
  photoInterests: {
    id: "photoInterests",
    title: "Fotoğraf odakları",
    subtitle: "Portre, manzara, sokak, astro...",
    required: false,
  },
  goldenHourPriority: {
    id: "goldenHourPriority",
    title: "Altın saat önceliği",
    subtitle: "Gün doğumu/batımı için rotayı ayarlayalım mı?",
    required: false,
  },
  specialRequests: {
    id: "specialRequests",
    title: "Özel istekleriniz var mı?",
    subtitle: "Ne olursa yazın; biz planlamaya çalışacağız.",
    required: false,
  },
};

export const QUESTION_FLOW: QuestionFlow = {
  core: ["destination", "departure", "dates", "travelers", "transport", "budget"],
  conditionalByTransport: {
    car: ["vehicleInfo", "transitStops"],
    caravan: ["vehicleInfo", "campingPreferences", "transitStops"],
    gulet: ["guletPreferences", "waterSports"],
    cruise: ["cruisePreferences"],
    bicycle: ["fitnessLevel", "bikeType"],
    trekking: ["fitnessLevel", "altitudeComfort"],
  },
  secondary: ["accommodation", "interests", "pace", "food"],
  conditionalByProfile: {
    family_fun: ["childFriendlyPriorities"],
    adventure: ["adventureTypes", "fitnessLevel"],
    wellness: ["wellnessTypes"],
    cultural_deep: ["culturalInterests"],
    gastro_tour: ["cuisineTypes", "cookingClasses"],
    photography: ["photoInterests", "goldenHourPriority"],
  },
  final: ["specialRequests"],
};
